import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Calendar, Clock, MapPin, ChevronDown, Award, Loader2, Check } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';

const SLUG = 'crea-tu-proteccion-energetica';

// ============== PALETA (Consciencia Editorial + Naturaleza + Arquitectura Viva)
const C = {
  white: '#FFFFFF',
  cream: '#FAF7F0',
  mineral: '#26332E',
  charcoal: '#252724',
  sage: '#8D9988',
  gold: '#B89A62',
  border: '#E6E2D8',
};

// ============== HELPERS
const fmtDate = (iso, tz = 'Europe/Madrid') => {
  if (!iso) return {};
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return {};
  const weekday = d.toLocaleDateString('es-ES', { weekday: 'long', timeZone: tz });
  const day = d.toLocaleDateString('es-ES', { day: 'numeric', timeZone: tz });
  const month = d.toLocaleDateString('es-ES', { month: 'long', timeZone: tz });
  const year = d.toLocaleDateString('es-ES', { year: 'numeric', timeZone: tz });
  const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: tz });
  return { weekday, day, month, year, time };
};
const eur = (cents, currency = 'eur') =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: 0 }).format((cents || 0) / 100);

// ============== ANIM PRIMITIVES
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

// ============== UI ATOMS
const Eyebrow = ({ children, dark = false, testid }) => (
  <span
    className={`inline-flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase font-medium ${dark ? 'text-[#B89A62]' : 'text-[#B89A62]'}`}
    data-testid={testid}
  >
    <span className="w-1 h-1 rounded-full bg-[#B89A62]" aria-hidden />
    {children}
  </span>
);

const HairLine = ({ className = '', tone = 'gold' }) => (
  <span
    aria-hidden
    className={`block h-px ${className}`}
    style={{ backgroundColor: tone === 'gold' ? `${C.gold}66` : `${C.border}` }}
  />
);

const Btn = ({ variant = 'dark', children, className = '', ...rest }) => {
  const styles = variant === 'dark'
    ? 'bg-[#26332E] hover:bg-[#1c2621] text-[#FAF7F0]'
    : variant === 'gold'
    ? 'bg-[#B89A62] hover:bg-[#a68949] text-white'
    : 'bg-white text-[#26332E] border border-[#26332E]/20 hover:border-[#26332E]';
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 sm:py-5 rounded-[6px] text-[12px] sm:text-[13px] tracking-[0.16em] uppercase font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 ${styles} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

// ============== TICKET (boarding-pass)
const Ticket = ({ dateInfo, endTime, location, tier, seatsLeft, currency = 'eur' }) => (
  <div className="relative bg-white border border-[#E6E2D8] rounded-2xl overflow-hidden shadow-[0_8px_32px_-16px_rgba(37,39,36,0.15)]">
    {/* corner detail */}
    <div className="absolute top-0 right-0 w-16 h-16 border-l border-b border-[#B89A62]/30 rounded-bl-[24px] pointer-events-none" aria-hidden />
    <div className="absolute top-4 right-4 text-[9px] tracking-[0.3em] text-[#B89A62] uppercase" aria-hidden>Escuela Akáshica</div>

    <div className="px-6 sm:px-8 pt-8 pb-6">
      <div className="flex items-baseline gap-5">
        <div className="flex-shrink-0">
          <div className="text-[9px] tracking-[0.3em] text-[#8D9988] uppercase mb-1">{dateInfo.weekday || 'Sábado'}</div>
          <div className="font-heading text-[64px] leading-none text-[#26332E]">{dateInfo.day || '7'}</div>
        </div>
        <div className="pt-2">
          <div className="text-[13px] tracking-[0.2em] text-[#26332E] uppercase font-medium">{dateInfo.month || 'Noviembre'}</div>
          <div className="text-[13px] tracking-[0.2em] text-[#8D9988] uppercase">{dateInfo.year || '2026'}</div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-6 text-[13px] text-[#252724]/80">
        <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#B89A62]" strokeWidth={1.5}/>{location || 'Online'}</span>
        <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#B89A62]" strokeWidth={1.5}/>{dateInfo.time || '10:00'}{endTime ? `–${endTime}` : ''}</span>
      </div>
    </div>

    {/* perforation */}
    <div className="relative">
      <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-[#FAF7F0] rounded-full border border-[#E6E2D8]" aria-hidden />
      <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-[#FAF7F0] rounded-full border border-[#E6E2D8]" aria-hidden />
      <div className="mx-8 border-t border-dashed border-[#B89A62]/40" />
    </div>

    <div className="px-6 sm:px-8 pt-6 pb-8">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-[9px] tracking-[0.3em] text-[#B89A62] uppercase mb-1.5">{tier?.name || 'LOTE'}</div>
          <div className="font-heading text-[44px] leading-none text-[#26332E]">{eur(tier?.amount_cents || 0, currency)}</div>
        </div>
        {seatsLeft !== null && (
          <div className="text-right">
            <div className="text-[10px] tracking-[0.2em] text-[#8D9988] uppercase">Plazas a este precio</div>
            <div className="text-[24px] font-heading text-[#26332E]">{seatsLeft}</div>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ============== MAIN
export default function MasterclassProteccionEnergetica() {
  const [cfg, setCfg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [withRecording, setWithRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    api.get(`/paid-workshops/${SLUG}`)
      .then(r => setCfg(r.data))
      .catch(() => setNotFound(true));
  }, []);

  const active = cfg?.active_tier;
  const startInfo = useMemo(() => fmtDate(cfg?.date_iso), [cfg?.date_iso]);
  const endInfo = useMemo(() => fmtDate(cfg?.end_iso), [cfg?.end_iso]);
  const seatsLeft = active ? Math.max(0, (active.seats_total || 0) - (active.seats_sold || 0)) : null;
  const totalCents = (active?.amount_cents || 0) + (withRecording && cfg?.recording_addon_enabled ? (cfg?.recording_addon_price_cents || 0) : 0);

  // sticky visibility: hide when the buy section is centered on the viewport
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const on = () => {
      const buy = document.getElementById('reserva');
      const scrolled = window.scrollY > 320;
      if (!buy) { setShowSticky(scrolled); return; }
      const rect = buy.getBoundingClientRect();
      const inView = rect.top < window.innerHeight * 0.6 && rect.bottom > window.innerHeight * 0.2;
      setShowSticky(scrolled && !inView);
    };
    window.addEventListener('scroll', on, { passive: true });
    on();
    return () => window.removeEventListener('scroll', on);
  }, []);

  const scrollToBuy = () => {
    const el = document.getElementById('reserva');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCheckout = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const { data } = await api.post(`/paid-workshops/${SLUG}/checkout`, {
        origin_url: window.location.origin,
        email: email || undefined,
        with_recording: withRecording,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar el pago.');
      setStarting(false);
    }
  };

  if (notFound) {
    return (
      <div className="pt-32 min-h-screen flex items-center justify-center bg-white px-6 text-center">
        <h1 className="text-2xl font-heading text-[#252724]">Este taller aún no está disponible</h1>
      </div>
    );
  }
  if (!cfg) {
    return <div className="pt-32 min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-6 h-6 animate-spin text-[#B89A62]" /></div>;
  }

  // Full price progression (for the price section)
  const progression = (cfg.price_tiers || []).map(t => Math.round((t.amount_cents || 0) / 100));

  return (
    <div className="min-h-screen bg-white text-[#252724] font-sans overflow-x-hidden">
      {/* ============== STICKY BAR ============== */}
      <div
        aria-hidden={!showSticky}
        className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
          showSticky ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none -translate-y-2 md:translate-y-0 md:translate-y-[-100%]'
        }`}
        style={{ top: 'env(safe-area-inset-top, 0)' }}
      >
        {/* Desktop */}
        <div className="hidden md:block bg-white/95 backdrop-blur-md border-b border-[#E6E2D8]">
          <div className="max-w-7xl mx-auto px-8 py-3 flex items-center gap-6">
            <div className="flex items-center gap-4 min-w-0">
              <div className="text-[10px] tracking-[0.3em] text-[#B89A62] uppercase">{active?.name}</div>
              <div className="font-heading text-2xl text-[#26332E]">{eur(active?.amount_cents || 0, active?.currency)}</div>
              {seatsLeft !== null && active?.seats_total > 0 && (
                <div className="flex items-center gap-3 text-[12px] text-[#252724]/70">
                  <div className="w-32 h-1 bg-[#E6E2D8] rounded-full overflow-hidden">
                    <div className="h-full bg-[#B89A62]" style={{ width: `${100 * (active.seats_sold || 0) / active.seats_total}%` }} />
                  </div>
                  <span>{seatsLeft} {seatsLeft === 1 ? 'plaza' : 'plazas'} a este precio</span>
                </div>
              )}
            </div>
            <div className="ml-auto">
              <Btn variant="dark" onClick={scrollToBuy} data-testid="pw-sticky-cta-desktop">
                Quiero mi plaza <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Btn>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom */}
      <div
        aria-hidden={!showSticky}
        className={`md:hidden fixed left-0 right-0 z-40 bg-white/97 backdrop-blur-md border-t border-[#E6E2D8] transition-transform duration-300 ${
          showSticky ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ bottom: 0, paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      >
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="text-[9px] tracking-[0.25em] text-[#B89A62] uppercase leading-none mb-0.5">{active?.name}</div>
            <div className="font-heading text-[26px] leading-none text-[#26332E]">{eur(active?.amount_cents || 0, active?.currency)}</div>
          </div>
          <button
            onClick={scrollToBuy}
            className="flex-1 bg-[#26332E] text-[#FAF7F0] text-[12px] tracking-[0.16em] uppercase font-semibold rounded-[6px] py-3.5"
            data-testid="pw-sticky-cta-mobile"
          >
            Quiero mi plaza
          </button>
        </div>
      </div>

      {/* ============== HERO ============== */}
      <section className="pt-24 sm:pt-32 pb-14 sm:pb-24 px-5 sm:px-8 lg:px-16 bg-white relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — content */}
          <motion.div className="lg:col-span-7 lg:order-1 order-2" {...fadeUp}>
            <Eyebrow testid="pw-hero-eyebrow">{cfg.eyebrow}</Eyebrow>
            <h1
              className="mt-5 sm:mt-6 font-heading text-[40px] leading-[1.08] sm:text-[56px] lg:text-[68px] lg:leading-[1.03] tracking-tight text-[#26332E]"
              data-testid="pw-hero-title"
            >
              {cfg.hero_headline}
            </h1>
            <p className="mt-6 sm:mt-8 text-[16px] sm:text-[18px] leading-[1.7] text-[#252724]/80 max-w-xl">
              {cfg.hero_body}
            </p>

            {cfg.hero_bullets?.length > 0 && (
              <ul className="mt-8 space-y-3.5 max-w-xl">
                {cfg.hero_bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 items-start text-[15px] sm:text-[16px] text-[#252724]/85 leading-relaxed">
                    <Check className="w-4 h-4 mt-1 text-[#B89A62] flex-shrink-0" strokeWidth={2} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Btn variant="dark" onClick={scrollToBuy} data-testid="pw-hero-cta">
                Quiero mi plaza <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Btn>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#8D9988]">Directo · Certificado incluido</span>
            </div>
          </motion.div>

          {/* RIGHT — Herminio + Ticket */}
          <motion.div
            className="lg:col-span-5 lg:order-2 order-1 relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative aspect-[4/5] max-w-[420px] mx-auto lg:mx-0 lg:ml-auto">
              {/* Fine golden membrane lines */}
              <svg viewBox="0 0 400 500" className="absolute -inset-4 sm:-inset-6 w-[calc(100%+2rem)] sm:w-[calc(100%+3rem)] h-[calc(100%+2rem)] sm:h-[calc(100%+3rem)] pointer-events-none" aria-hidden>
                <ellipse cx="200" cy="250" rx="196" ry="246" fill="none" stroke={C.gold} strokeOpacity="0.32" strokeWidth="0.75" />
                <ellipse cx="200" cy="250" rx="188" ry="236" fill="none" stroke={C.gold} strokeOpacity="0.18" strokeWidth="0.5" />
              </svg>
              {cfg.author_image_url && (
                <img
                  src={cfg.author_image_url}
                  alt={cfg.author_name}
                  loading="eager"
                  className="relative w-full h-full object-cover rounded-[6px]"
                />
              )}
              <div className="absolute inset-0 rounded-[6px] ring-1 ring-[#B89A62]/25 pointer-events-none" aria-hidden />
            </div>

            {/* Ticket — floating below photo (mobile) or beside (lg) */}
            {active && (
              <motion.div
                className="mt-8 lg:mt-6 max-w-[420px] mx-auto lg:ml-auto"
                {...fadeUp}
                data-testid="pw-hero-ticket"
              >
                <Ticket dateInfo={startInfo} endTime={endInfo.time} location={cfg.location} tier={active} seatsLeft={seatsLeft} currency={active.currency} />
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ============== CAMINO — DARK ============== */}
      <section className="bg-[#26332E] text-[#FAF7F0] py-20 sm:py-28 px-5 sm:px-8 lg:px-16 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute top-10 left-10 w-32 h-px bg-[#B89A62]/40" />
          <div className="absolute bottom-10 right-10 w-32 h-px bg-[#B89A62]/40" />
        </div>
        <motion.div className="max-w-3xl mx-auto relative" {...fadeUp}>
          <Eyebrow dark>El camino</Eyebrow>
          <div className="mt-6 space-y-5 text-[17px] sm:text-[19px] leading-[1.75] whitespace-pre-wrap text-[#FAF7F0]/92 font-serif">
            {cfg.camino_intro}
          </div>
        </motion.div>
      </section>

      {/* ============== SI TE RECONOCES ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16 bg-[#FAF7F0]">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            className="font-heading text-[28px] leading-[1.2] sm:text-[36px] lg:text-[44px] text-[#26332E] max-w-3xl leading-snug mb-14 sm:mb-16"
            {...fadeUp}
          >
            {cfg.reconoces_intro}
          </motion.h2>

          <div className="space-y-10 sm:space-y-14">
            {(cfg.reconoces_items || []).map((it, i) => (
              <motion.div key={i} className="grid sm:grid-cols-12 gap-6 sm:gap-10 items-start" {...fadeUp}>
                <div className="sm:col-span-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#8D9988] mb-3">Antes</div>
                  <p className="text-[16px] sm:text-[17px] italic leading-relaxed text-[#252724]/70 font-serif">
                    {it.antes}
                  </p>
                </div>
                <div className="hidden sm:block sm:col-span-1 pt-6">
                  <div className="w-full h-px bg-[#B89A62]/40" />
                </div>
                <div className="sm:col-span-6 sm:pl-2">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#B89A62] mb-3">Después</div>
                  <p className="text-[17px] sm:text-[19px] leading-relaxed text-[#26332E] font-serif">
                    {it.despues}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div className="text-center mt-16" {...fadeUp}>
            <Btn variant="gold" onClick={scrollToBuy} data-testid="pw-reconoces-cta">
              Esto es para mí <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Btn>
          </motion.div>
        </div>
      </section>

      {/* ============== 01–05 STEPS ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="max-w-3xl mb-14 sm:mb-20">
            <Eyebrow>Un día completo</Eyebrow>
            <h2 className="mt-5 font-heading text-[32px] leading-[1.15] sm:text-[42px] lg:text-[52px] text-[#26332E] tracking-tight">
              Un día para observar, probar y crear.
            </h2>
            <p className="mt-6 text-[16px] sm:text-[17px] leading-[1.7] text-[#252724]/75 max-w-2xl">
              {cfg.what_youll_do_intro}
            </p>
          </motion.div>

          {/* Steps: horizontal 5-col on desktop, vertical on mobile */}
          <div className="space-y-14 sm:space-y-0 sm:grid sm:grid-cols-1 lg:grid-cols-5 sm:gap-8 lg:gap-6">
            {(cfg.what_youll_do_items || []).map((it, i) => (
              <motion.div key={i} className="relative" {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
                <div
                  className="font-heading text-[80px] sm:text-[88px] lg:text-[104px] leading-none tracking-tighter text-[#26332E]/10 select-none pointer-events-none"
                  aria-hidden
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="mt-3 lg:pr-4">
                  <div className="w-6 h-px bg-[#B89A62] mb-4" aria-hidden />
                  <h3 className="font-heading text-[19px] sm:text-[20px] text-[#26332E] leading-snug mb-2.5">{it.title}</h3>
                  <p className="text-[14px] sm:text-[15px] text-[#252724]/70 leading-relaxed">{it.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {cfg.duration_summary && (
            <motion.p className="mt-16 max-w-2xl italic text-[15px] text-[#8D9988]" {...fadeUp}>
              {cfg.duration_summary}
            </motion.p>
          )}
        </div>
      </section>

      {/* ============== AGENDA TIMELINE ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16 bg-[#FAF7F0]">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp} className="mb-12 sm:mb-16">
            <Eyebrow>{cfg.agenda_intro || 'Así será el día'}</Eyebrow>
            <h2 className="mt-4 font-heading text-[30px] sm:text-[38px] lg:text-[44px] text-[#26332E] leading-tight capitalize">
              {startInfo.weekday || 'Sábado'} {startInfo.day || '7'} de {startInfo.month || 'noviembre'}
            </h2>
            <p className="mt-3 text-[13px] sm:text-[14px] tracking-[0.15em] text-[#8D9988] uppercase">
              {cfg.location} · {startInfo.time}{endInfo.time ? `–${endInfo.time}` : ''}
            </p>
          </motion.div>

          <ol className="relative pl-8 sm:pl-10">
            <span className="absolute left-[7px] sm:left-[11px] top-1.5 bottom-1.5 w-px bg-[#B89A62]/30" aria-hidden />
            {(cfg.agenda_items || []).map((a, i) => {
              const highlight = a.time === '15:40' || a.time === '17:35';
              const softer = ['Pausa para comer', 'Descanso', 'Preguntas e integración', 'Integración y siguientes pasos', 'Integración final y siguientes pasos', 'Preguntas y cierre', 'Fin del taller'].includes(a.title);
              return (
                <motion.li
                  key={i}
                  className={`relative ${i === 0 ? '' : 'mt-6 sm:mt-7'}`}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.3) }}
                >
                  <span
                    className={`absolute -left-8 sm:-left-10 top-[9px] w-[15px] h-[15px] rounded-full border-2 border-[#FAF7F0] ${
                      highlight ? 'bg-[#B89A62]' : softer ? 'bg-[#8D9988]/70' : 'bg-[#26332E]'
                    }`}
                    aria-hidden
                  />
                  {highlight ? (
                    <div className="bg-[#26332E] text-[#FAF7F0] rounded-2xl p-5 sm:p-6 shadow-sm border border-[#B89A62]/40">
                      <div className="text-[11px] tracking-[0.25em] text-[#B89A62] uppercase font-medium">{a.time}</div>
                      <h4 className="mt-2 font-heading text-[19px] sm:text-[22px] leading-snug">{a.title}</h4>
                      {a.description && <p className="mt-2 text-[14px] leading-relaxed text-[#FAF7F0]/85">{a.description}</p>}
                    </div>
                  ) : (
                    <div className={softer ? 'opacity-55' : ''}>
                      <div className="text-[11px] tracking-[0.25em] text-[#B89A62] uppercase font-medium">{a.time}</div>
                      <h4 className={`mt-1.5 font-heading ${softer ? 'text-[15px] sm:text-[16px]' : 'text-[17px] sm:text-[19px]'} text-[#26332E] leading-snug`}>{a.title}</h4>
                      {a.description && !softer && <p className="mt-1.5 text-[14px] text-[#252724]/70 leading-relaxed">{a.description}</p>}
                    </div>
                  )}
                </motion.li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ============== TESTIMONIOS 1 (editorial) ============== */}
      {(cfg.testimonials_1 || []).length > 0 && (
        <section className="py-24 sm:py-32 px-5 sm:px-8 lg:px-16 bg-white">
          <div className="max-w-5xl mx-auto">
            <motion.p className="text-[15px] sm:text-[16px] leading-relaxed text-[#252724]/70 max-w-2xl mb-16 sm:mb-20" {...fadeUp}>
              {cfg.testimonials_1_intro}
            </motion.p>

            {/* Featured quote (first) — huge editorial */}
            {cfg.testimonials_1[0] && (
              <motion.blockquote className="mb-16 sm:mb-20 max-w-4xl" {...fadeUp}>
                <div className="font-heading text-[64px] leading-none text-[#B89A62] mb-2" aria-hidden>“</div>
                <p className="font-heading text-[26px] sm:text-[36px] lg:text-[44px] leading-[1.2] text-[#26332E] tracking-tight -mt-2">
                  {cfg.testimonials_1[0].text}
                </p>
                <footer className="mt-6 text-[11px] tracking-[0.25em] uppercase text-[#8D9988]">
                  {cfg.testimonials_1[0].name}{cfg.testimonials_1[0].context ? ` · ${cfg.testimonials_1[0].context}` : ''}
                </footer>
              </motion.blockquote>
            )}

            {/* Two smaller quotes */}
            {cfg.testimonials_1.length > 1 && (
              <div className="grid md:grid-cols-2 gap-10 sm:gap-14 pt-10 border-t border-[#E6E2D8]">
                {cfg.testimonials_1.slice(1).map((t, i) => (
                  <motion.blockquote key={i} className="max-w-xl" {...fadeUp}>
                    <p className="font-serif italic text-[17px] sm:text-[18px] leading-[1.65] text-[#252724]/85">
                      «{t.text}»
                    </p>
                    <footer className="mt-4 text-[11px] tracking-[0.25em] uppercase text-[#8D9988]">
                      {t.name}{t.context ? ` · ${t.context}` : ''}
                    </footer>
                  </motion.blockquote>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============== RESERVA / CHECKOUT — DARK MINERAL ============== */}
      <section id="reserva" className="scroll-mt-16 bg-[#26332E] text-[#FAF7F0] py-24 sm:py-32 px-5 sm:px-8 lg:px-16 relative overflow-hidden">
        {/* fine gold framing lines */}
        <div className="absolute inset-6 sm:inset-10 pointer-events-none border border-[#B89A62]/12 rounded-[2px]" aria-hidden />

        <div className="max-w-4xl mx-auto relative">
          <motion.div className="text-center" {...fadeUp}>
            <Eyebrow dark>Reserva tu plaza</Eyebrow>
            <h2 className="mt-4 font-heading text-[30px] sm:text-[44px] lg:text-[54px] leading-[1.08] tracking-tight text-[#FAF7F0]">
              Crea en un día tu protección energética
            </h2>
            <p className="mt-5 text-[14px] tracking-[0.15em] uppercase text-[#B89A62] capitalize">
              {startInfo.weekday} {startInfo.day} de {startInfo.month} de {startInfo.year} · {cfg.location} · {startInfo.time}{endInfo.time ? `–${endInfo.time}` : ''}
            </p>
          </motion.div>

          {/* Big price */}
          {active && (
            <motion.div className="text-center mt-14 sm:mt-20" {...fadeUp}>
              <div className="text-[11px] tracking-[0.35em] uppercase text-[#B89A62] mb-3">{active.name}</div>
              <div className="font-heading text-[96px] sm:text-[140px] lg:text-[168px] leading-none text-[#FAF7F0]">
                {eur(active.amount_cents, active.currency)}
              </div>
              {seatsLeft !== null && active.seats_total > 0 && (
                <p className="mt-4 text-[14px] text-[#FAF7F0]/70">{seatsLeft} {seatsLeft === 1 ? 'plaza disponible' : 'plazas disponibles'} a este precio.</p>
              )}
            </motion.div>
          )}

          {/* Progression 9 → 15 */}
          {progression.length > 1 && (
            <motion.div className="mt-14 sm:mt-16 max-w-3xl mx-auto" {...fadeUp}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#B89A62] text-center mb-5">Progresión de precio por lote</div>
              <div className="flex items-center justify-between gap-1 sm:gap-2">
                {(() => {
                  const start = progression[0];
                  const end = progression[progression.length - 1];
                  const steps = [];
                  for (let v = start; v <= end; v++) {
                    steps.push(v);
                  }
                  return steps.map((v, i) => {
                    const active_ = v === Math.round((active?.amount_cents || 0) / 100);
                    return (
                      <div key={i} className="flex flex-col items-center flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full mb-2 ${active_ ? 'bg-[#B89A62]' : 'bg-[#FAF7F0]/25'}`} aria-hidden />
                        <div className={`text-[13px] sm:text-[15px] font-heading ${active_ ? 'text-[#FAF7F0]' : 'text-[#FAF7F0]/40'}`}>{v} €</div>
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="mt-4 h-px w-full bg-[#B89A62]/25" />
            </motion.div>
          )}

          {/* Lo que te llevas */}
          {(cfg.what_you_get_items || []).length > 0 && (
            <motion.div className="mt-16 sm:mt-20 max-w-2xl mx-auto" {...fadeUp}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#B89A62] mb-6 text-center">{cfg.what_you_get_intro || 'Lo que te llevas'}</div>
              <ul className="space-y-4">
                {cfg.what_you_get_items.map((it, i) => (
                  <li key={i} className="flex gap-4 items-start text-[15px] sm:text-[16px] leading-[1.7] text-[#FAF7F0]/92">
                    <span className="mt-2 w-5 h-px bg-[#B89A62] flex-shrink-0" aria-hidden />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Form */}
          <motion.div className="mt-16 sm:mt-20 max-w-lg mx-auto space-y-5" {...fadeUp}>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-[#B89A62] mb-2">Tu email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="w-full px-4 py-3.5 rounded-[6px] bg-white/5 border border-[#FAF7F0]/25 text-[#FAF7F0] placeholder-[#FAF7F0]/40 text-[15px] focus:outline-none focus:border-[#B89A62] focus:bg-white/8 transition-colors"
                data-testid="pw-email-input"
              />
              <p className="mt-2 text-[12px] text-[#FAF7F0]/55">Recibirás tu enlace mágico para entrar a «Mi Camino».</p>
            </div>

            {cfg.recording_addon_enabled && cfg.recording_addon_price_cents > 0 && (
              <label className="flex items-start gap-3 p-4 rounded-[6px] border border-[#FAF7F0]/20 bg-white/5 cursor-pointer hover:border-[#B89A62]/60 transition-colors">
                <input
                  type="checkbox"
                  checked={withRecording}
                  onChange={(e) => setWithRecording(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#B89A62]"
                  data-testid="pw-recording-checkbox"
                />
                <span className="text-[14px] text-[#FAF7F0]/90 leading-relaxed">
                  <span className="font-medium text-[#FAF7F0]">Añadir grabación del taller</span>
                  <span className="text-[#B89A62]"> · {eur(cfg.recording_addon_price_cents, active?.currency || 'eur')}</span>
                  <br />
                  <span className="text-[12px] text-[#FAF7F0]/60">Podrás verla después del directo, cuando esté lista.</span>
                </span>
              </label>
            )}

            <div className="pt-2 text-center">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#B89A62] mb-1">Total</div>
              <div className="font-heading text-4xl text-[#FAF7F0]">{eur(totalCents, active?.currency || 'eur')}</div>
            </div>

            <button
              type="button"
              onClick={handleCheckout}
              disabled={starting || !active}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#B89A62] hover:bg-[#a68949] disabled:opacity-60 text-white px-8 py-5 rounded-[6px] text-[13px] tracking-[0.16em] uppercase font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
              data-testid="pw-checkout-cta"
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {starting ? 'Redirigiendo…' : `Quiero mi plaza por ${eur(active?.amount_cents || 0, active?.currency)}`}
              {!starting && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
            <p className="text-center text-[11px] text-[#FAF7F0]/50">Pago seguro con Stripe · Recibirás tu acceso por email.</p>
          </motion.div>
        </div>
      </section>

      {/* ============== WHY CHEAP ============== */}
      {cfg.why_cheap && (
        <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16 bg-white">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div {...fadeUp}>
              <div className="mx-auto w-12 h-px bg-[#B89A62] mb-6" aria-hidden />
              <h3 className="font-heading text-[28px] sm:text-[36px] text-[#26332E] mb-8 leading-tight">¿Por qué tan barato?</h3>
              <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#252724]/80 whitespace-pre-wrap">{cfg.why_cheap}</p>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============== TESTIMONIOS 2 ============== */}
      {(cfg.testimonials_2 || []).length > 0 && (
        <section className="py-24 sm:py-32 px-5 sm:px-8 lg:px-16 bg-[#FAF7F0]">
          <div className="max-w-5xl mx-auto">
            <motion.p className="text-[15px] sm:text-[16px] leading-relaxed text-[#252724]/70 max-w-2xl mb-14 sm:mb-16" {...fadeUp}>
              {cfg.testimonials_2_intro}
            </motion.p>

            <div className="grid md:grid-cols-2 gap-10 sm:gap-14">
              {cfg.testimonials_2.slice(0, 2).map((t, i) => (
                <motion.blockquote key={i} className="max-w-xl" {...fadeUp}>
                  <div className="font-heading text-[44px] leading-none text-[#B89A62] mb-2" aria-hidden>“</div>
                  <p className="font-serif italic text-[18px] sm:text-[20px] leading-[1.55] text-[#26332E] -mt-2">
                    {t.text}
                  </p>
                  <footer className="mt-5 text-[11px] tracking-[0.25em] uppercase text-[#8D9988]">
                    {t.name}{t.context ? ` · ${t.context}` : ''}
                  </footer>
                </motion.blockquote>
              ))}
            </div>

            {cfg.testimonials_2[2] && (
              <motion.blockquote className="mt-16 pt-14 border-t border-[#E6E2D8] max-w-3xl" {...fadeUp}>
                <p className="font-heading text-[22px] sm:text-[30px] leading-[1.3] text-[#26332E]">
                  «{cfg.testimonials_2[2].text}»
                </p>
                <footer className="mt-5 text-[11px] tracking-[0.25em] uppercase text-[#8D9988]">
                  {cfg.testimonials_2[2].name}{cfg.testimonials_2[2].context ? ` · ${cfg.testimonials_2[2].context}` : ''}
                </footer>
              </motion.blockquote>
            )}
          </div>
        </section>
      )}

      {/* ============== SOBRE HERMINIO ============== */}
      <section className="py-24 sm:py-32 px-5 sm:px-8 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {cfg.author_image_url && (
            <motion.div className="md:col-span-5" {...fadeUp}>
              <div className="relative aspect-[4/5] rounded-[6px] overflow-hidden">
                <img src={cfg.author_image_url} alt={cfg.author_name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-[#B89A62]/25 rounded-[6px]" aria-hidden />
              </div>
            </motion.div>
          )}
          <motion.div className={cfg.author_image_url ? 'md:col-span-7' : 'md:col-span-12'} {...fadeUp}>
            <Eyebrow>Sobre mí</Eyebrow>
            <h2 className="mt-4 font-heading text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.1] text-[#26332E]">
              Soy {cfg.author_name}
            </h2>
            <div className="mt-8 space-y-5 text-[16px] sm:text-[17px] leading-[1.75] text-[#252724]/80 whitespace-pre-wrap">
              {cfg.author_bio}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============== CERTIFICADO ============== */}
      {cfg.certificate_text && (
        <section className="py-16 sm:py-20 px-5 sm:px-8 lg:px-16 bg-[#FAF7F0]">
          <motion.div className="max-w-2xl mx-auto text-center" {...fadeUp}>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[#B89A62]/40 mb-5">
              <Award className="w-5 h-5 text-[#B89A62]" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-[22px] sm:text-[28px] text-[#26332E] mb-4">Certificado de participación</h3>
            <p className="text-[15px] sm:text-[16px] text-[#252724]/75 leading-relaxed">{cfg.certificate_text}</p>
          </motion.div>
        </section>
      )}

      {/* ============== FAQ ============== */}
      {(cfg.faqs || []).length > 0 && (
        <section className="py-24 sm:py-32 px-5 sm:px-8 lg:px-16 bg-white">
          <div className="max-w-2xl mx-auto">
            <motion.h2 className="font-heading text-[30px] sm:text-[38px] text-[#26332E] mb-10 sm:mb-12" {...fadeUp}>
              Preguntas frecuentes
            </motion.h2>
            <div className="divide-y divide-[#E6E2D8] border-t border-b border-[#E6E2D8]">
              {cfg.faqs.map((item, i) => (
                <details key={i} className="group py-5">
                  <summary className="flex items-center justify-between cursor-pointer list-none gap-6">
                    <span className="text-[15px] sm:text-[17px] text-[#26332E] font-medium leading-snug pr-4">{item.question}</span>
                    <ChevronDown className="w-4 h-4 text-[#B89A62] transition-transform group-open:rotate-180 flex-shrink-0" strokeWidth={1.5} />
                  </summary>
                  <p className="text-[14px] sm:text-[15px] text-[#252724]/75 mt-4 leading-[1.7] whitespace-pre-wrap">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== CIERRE — DARK ============== */}
      <section className="py-28 sm:py-40 px-5 sm:px-8 lg:px-16 bg-[#26332E] text-[#FAF7F0] relative overflow-hidden">
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div {...fadeUp}>
            <div className="mx-auto w-12 h-px bg-[#B89A62] mb-8" aria-hidden />
            <h2 className="font-heading text-[32px] sm:text-[46px] lg:text-[58px] leading-[1.08] text-[#FAF7F0] tracking-tight">
              {cfg.closing_headline}
            </h2>
            <p className="mt-8 text-[16px] sm:text-[18px] leading-[1.75] text-[#FAF7F0]/80 whitespace-pre-wrap max-w-2xl mx-auto">
              {cfg.closing_body}
            </p>
            <p className="mt-10 text-[12px] tracking-[0.2em] uppercase text-[#B89A62] capitalize">
              {startInfo.weekday} {startInfo.day} de {startInfo.month} de {startInfo.year} · {cfg.location} · {startInfo.time}{endInfo.time ? `–${endInfo.time}` : ''}
            </p>
            {active && <p className="mt-2 text-[13px] tracking-[0.15em] uppercase text-[#B89A62]">{active.name} · {eur(active.amount_cents, active.currency)}</p>}
            <div className="mt-10">
              <Btn variant="gold" onClick={scrollToBuy} data-testid="pw-closing-cta">
                Quiero mi plaza <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Btn>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
